import { getStyleGuideWordLimit, validateStyleGuideLength, styleGuideSaveErrorMessage } from "../../../../src/shared/styleGuideWordLimit";
import { composeSimpleStyleInstructions, normalizeSimpleStyleControls, type SimpleStyleControls } from "../../../../src/shared/simpleStyleControls";
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Link, useLocation } from "react-router-dom";
import InlineSVG from "react-inlinesvg";
import styled, { createGlobalStyle } from "styled-components/macro";
import { useShallow } from "zustand/react/shallow";

import { STORY_BIBLE_STYLE_ID, STORY_BIBLE_STYLE_NAME, STYLE_GUIDE_BUILT_IN_STYLES } from "../../../../src/shared/styleGuideBuiltInStyles.js";
import { DEFAULT_WRITE_MODEL } from "../../../../src/shared/constants.js";
import {
  DEFAULT_STYLE_GUIDE_GENERATE_SAMPLE_WRITE_MODEL,
  STYLE_GUIDE_GENERATE_SAMPLE_MODEL_OPTIONS,
  normalizeStyleGuideGenerateSampleWriteModel,
} from "../../../../src/shared/styleGuideGenerateSampleModels.js";
import Auth from "../../Auth";
import IconButton from "../IconButton";
import { useAllFoldersStore } from "../AppHome/store";
import ProjectCoverDialog from "../AppHome/ProjectCard/ProjectCoverDialog";
import ProjectCover from "../AppHome/ProjectCover";
import { AddNewStyleModal } from "../StyleGuide/AddNewStyleModal";
import { readGeneratedSampleStorage, writeGeneratedSampleStorage } from "../StyleGuide/generatedSampleStorage";
import { SampleGenerationStatus, StyleEditorSurface, type StyleEditorTab } from "../StyleGuide/StyleEditorSurface";
import StyleMatcher from "../StoryBible/StyleMatcher";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { useStoryBibleStore } from "../../stores/storyBibleStore";
import useWriteModel from "../../hooks/useWriteModel";

async function copyTextToClipboard(text: string): Promise<boolean> {
  if (!text || typeof navigator === "undefined" || !navigator.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export type SaveState = "saved" | "saving" | "saved-locally" | null;

type StyleGuideSelection =
  | { source: "story_bible"; id: string; name: string }
  | { source: "sudowrite"; id: string; name: string }
  | { source: "user_style"; id: string; name: string }
  | { source: "custom"; name: string };

type StyleGuideStyleListItem = {
  source: "story_bible" | "sudowrite" | "user_style" | "custom";
  id?: string;
  name: string;
  tag?: string;
  kind?: string;
  instructions?: string;
  writingSample?: string;
  simpleControls?: SimpleStyleControls;
};

type EditableStylePayload = {
  id?: string;
  name: string;
  instructions: string;
  writingSample?: string;
  simpleControls?: SimpleStyleControls;
  source?: "blank" | "generated";
};

interface ProjectLike {
  id?: string;
  title: string;
  metadata?: { writeProseMode?: string };
  data?: { hasAutomaticTitle?: boolean; title?: string };
  update: (patch: { title: string; hasAutomaticTitle?: boolean }) => unknown;
  isUntitled?: () => boolean;
  generateProjectTitle?: () => string | null;
}

interface ProjectTopBarProps {
  project: ProjectLike;
  saved: SaveState;
  wordCount: number | null;
  highlightedWordCount: number;
  showWordCount: boolean;
  collapsed?: boolean;
  showStyleGuideControl?: boolean;
  selectedStyleName?: string | null;
  selectedStyle?: StyleGuideSelection | null;
  styleGuideStyles?: StyleGuideStyleListItem[];
  styleGuideOpen?: boolean;
  /**
   * Optional render slot — when provided, replaces the default share IconButton.
   * Use this to drop in <DocumentSharingMenu /> which renders its own trigger + popover.
   */
  shareSlot?: React.ReactNode;
  helpSlot?: React.ReactNode;
  settingsSlot?: React.ReactNode;
  tourActionSlot?: React.ReactNode;
  /**
   * Keep the share/help/settings icons visible next to the tour action slot
   * (used by tours that point at those icons, e.g. paragraph spacing). The
   * save/word-count status still hides to make room.
   */
  tourActionKeepsIcons?: boolean;
  /** Click handler for the default share IconButton (ignored when shareSlot is provided). */
  onShareClick?: () => void;
  onHelpClick?: () => void;
  onSettingsClick?: () => void;
  onStyleGuideClick?: () => void;
  onStyleGuideSettingsClick?: () => void;
  onStyleGuideSelect?: (style: StyleGuideSelection) => Promise<void> | void;
  onBlankStyleSave?: (style: EditableStylePayload) => Promise<StyleGuideStyleListItem | void> | StyleGuideStyleListItem | void;
  onStoryBibleStyleSave?: (style: { instructions: string; simpleControls?: SimpleStyleControls }) => Promise<StyleGuideStyleListItem | void> | StyleGuideStyleListItem | void;
  onGeneratedStyleCreated?: (style: StyleGuideStyleListItem) => void;
  initialStyleGuideFlow?: StylesFlowState;
  initialStyleGuideFlowRequestId?: number;
  onProjectTitleChange?: () => void;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  box-sizing: border-box;
  height: 48px;
  padding: 12px 16px;
  background-color: transparent;
  box-shadow: none;
  position: relative;
  z-index: 20000;
  margin-top: 0;
  transition: margin-top 0.3s;

  &.collapsed {
    margin-top: -48px;
    pointer-events: none;
  }
`;

const ProjectCoverButton = styled.button`
  all: unset;
  box-sizing: border-box;
  flex: 0 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  margin-left: -4px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--primary-cta);
    outline-offset: 2px;
  }
`;

const LeftCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  flex: 1 1 auto;
`;

const RightCluster = styled.div<{ $styleGuideOpen?: boolean }>`
  position: absolute;
  top: 12px;
  right: ${({ $styleGuideOpen }) => ($styleGuideOpen ? "-15px" : "16px")};
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 0 0 auto;
`;

const CenterCluster = styled.div`
  position: absolute;
  left: 50%;
  top: 13.5px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  pointer-events: none;

  @media (max-width: 980px) {
    transform: translateX(-50%);
  }
`;

const StyleGuideControlWrap = styled.div`
  position: relative;
  pointer-events: auto;
`;

const StyleGuideButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 21px;
  max-width: 260px;
  padding: 0;
  border: 0 !important;
  border-radius: 6px !important;
  background: transparent !important;
  color: ${({ $active }) => ($active ? "var(--primary-cta-high-contrast)" : "var(--grey-1)")} !important;
  font: inherit;
  font-size: 13.9px !important;
  font-weight: 500;
  line-height: 16.943px !important;
  cursor: pointer;
  box-shadow: none;

  &:hover,
  &:focus-visible {
    color: var(--primary-cta-high-contrast) !important;
    background: transparent !important;
    outline: none;
  }

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
  }

  .style-guide-leading-icon {
    transform: translateY(0.65px) scale(1.25);
    transform-origin: center;
  }

  svg path {
    fill: currentColor;
  }

  .style-guide-selected-name {
    font-size: 13.9px;
    font-weight: 500;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .style-guide-chevron {
    width: 16px;
    height: 16px;
    transform: ${({ $active }) => ($active ? "rotate(180deg)" : "none")};
  }
`;

const TopFilterSeparator = styled.span`
  display: inline-flex;
  width: 1px;
  height: 12px;
  background: var(--grey-3);
  pointer-events: none;
`;

const TopFilterButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 21px;
  padding: 0;
  border: 0 !important;
  border-radius: 6px !important;
  background: transparent !important;
  color: ${({ $active }) => ($active ? "var(--primary-cta-high-contrast)" : "var(--grey-1)")} !important;
  font: inherit;
  font-size: 13.9px !important;
  font-weight: 500;
  line-height: 16.943px !important;
  cursor: pointer;
  pointer-events: auto;

  &:hover,
  &:focus-visible {
    color: var(--primary-cta-high-contrast) !important;
    background: transparent !important;
    outline: none;
  }

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
  }

  .top-filter-leading-icon {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
  }

  .top-filter-quality-label {
    display: inline-block;
    font-size: 13.9px;
    font-weight: 500;
  }

  .top-filter-chevron {
    width: 16px;
    height: 16px;
    transform: ${({ $active }) => ($active ? "rotate(180deg)" : "none")};
  }

`;

const TopFilterStaticDisplay = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 21px;
  padding: 0;
  color: var(--grey-1);
  font: inherit;
  font-size: 13.9px;
  font-weight: 500;
  line-height: 16.943px;
  cursor: default;

  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
  }

  .top-filter-leading-icon {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
  }

  .top-filter-quality-label {
    display: inline-block;
    font-size: 13.9px;
    font-weight: 500;
  }
`;

const StylesStatePanel = styled.div`
  position: fixed;
  inset: 0;
  z-index: 19900;
  padding: 84px 32px 48px;
  box-sizing: border-box;
  overflow: auto;
  background: transparent;
  color: var(--grey-0);
  pointer-events: auto;
`;

const StylesStateContent = styled.div`
  --styles-overlay-x: 0px;

  width: min(800px, calc(100vw - 64px));
  margin: 0 auto;
  opacity: 0;
  pointer-events: none;
  transform: translateX(var(--styles-overlay-x)) translateY(-18px) scale(0.965);
  transform-origin: 50% -32px;
  transition:
    opacity 180ms ease,
    transform 220ms ease;
  will-change: transform, opacity;

  ${StylesStatePanel}[data-open="true"] & {
    opacity: 1;
    pointer-events: auto;
    transform: translateX(var(--styles-overlay-x)) translateY(0) scale(1);
    transition:
      opacity 680ms ease 190ms,
      transform 980ms cubic-bezier(0.16, 0.9, 0.18, 1) 160ms;
  }

  ${StylesStatePanel}[data-modal-open="true"] & {
    opacity: 0;
    pointer-events: none;
  }

  @media (max-width: 980px) {
    --styles-overlay-x: 0px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 1ms;
    transition-delay: 0ms;

    ${StylesStatePanel}[data-open="true"] & {
      transition-duration: 1ms;
      transition-delay: 0ms;
      transform: translateX(var(--styles-overlay-x));
    }
  }
`;

const StylesPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const StylesPanelTitle = styled.h2`
  display: inline-flex;
  align-items: center;
  gap: 1px;
  margin: 0;
  color: var(--grey-0);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;

  svg {
    width: 32px;
    height: 32px;
    transform: translateX(-3px);
  }

  svg path {
    fill: currentColor !important;
  }
`;

const StylesPanelActions = styled.div`
  display: inline-flex;
  gap: 6px;
`;

const MoreStateContent = styled(StylesStateContent)`
  --styles-overlay-x: 0px;

  width: min(800px, calc(100vw - 64px));
`;

const MoreRows = styled.div`
  display: grid;
  gap: 12px;
`;

const MoreRow = styled.div`
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 16px;
  min-height: 75px;
  padding: 0 20px;
  box-sizing: border-box;
  border: 1px solid var(--grey-3);
  border-radius: 8px;
  background: var(--menu-color);
  box-shadow: var(--default-drop-shadow);
  color: var(--grey-0);

  > .more-row-icon {
    width: 27px;
    height: 27px;
    color: var(--grey-0);
  }

  path {
    fill: currentColor;
  }
`;

const MoreRowText = styled.div`
  display: grid;
  gap: 2px;
  min-width: 0;
  text-align: left;

  strong {
    color: var(--grey-0);
    font-size: 17px;
    font-weight: 500;
    line-height: 20px;
  }

  span {
    color: var(--grey-1);
    font-size: 15px;
    font-weight: 400;
    line-height: 19px;
  }
`;

const WordsToAvoidActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 16px;
`;

const WordsToAvoidGroupButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 10px;
  border: 1px solid var(--grey-3);
  border-radius: 8px;
  background: var(--menu-color);
  color: var(--grey-0);
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }

  path {
    fill: var(--grey-2);
  }

  &:hover,
  &:focus-visible {
    border-color: var(--primary-cta);
    outline: none;
  }
`;

const MoreIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--grey-0);
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }

  path {
    fill: currentColor;
  }

  &:hover,
  &:focus-visible {
    background: var(--primary-cta-translucent);
    color: var(--primary-cta-high-contrast);
    outline: none;
  }
`;

const LimitRepetitionSwitch = styled.input<{ $checked: boolean }>`
  position: relative;
  width: 42px;
  height: 22px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 999px;
  appearance: none;
  background: ${({ $checked }) => ($checked ? "var(--primary-cta)" : "var(--grey-3)")};
  cursor: pointer;
  transition: background 160ms ease;

  &::after {
    content: "";
    position: absolute;
    top: 1px;
    left: ${({ $checked }) => ($checked ? "21px" : "1px")};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--menu-color);
    box-shadow: var(--tiny-drop-shadow);
    transition: left 160ms ease;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-cta);
    outline-offset: 2px;
  }
`;

const StylesSearchHeader = styled.div`
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) 32px;
  align-items: center;
  column-gap: 12px;
  width: 100%;
  min-height: 32px;
  border-bottom: 1px solid var(--primary-cta);
  color: var(--grey-0);

  svg {
    width: 24px;
    height: 24px;
  }

  svg path {
    fill: currentColor !important;
  }
`;

const StylesSearchInput = styled.input`
  width: 100%;
  height: 32px;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--grey-0);
  font: inherit;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`;

const StylesSearchCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--grey-0);
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: var(--menu-color-hover);
    color: var(--grey-0);
    outline: none;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const StylesSearchEmpty = styled.div`
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 0 16px;
  color: var(--grey-1);
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`;

const StylesIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--grey-0);
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: var(--primary-cta-translucent);
    color: var(--primary-cta-high-contrast);
    outline: none;
  }

  svg {
    width: 28px;
    height: 28px;
  }

  .styles-panel-action-icon-search {
    transform: translate(0.18px, -0.2px) scale(1.25);
    transform-origin: center;
  }

  .styles-panel-action-icon-add {
    transform-origin: center;
  }

  svg [opacity] {
    opacity: 1;
  }

  svg path {
    fill: currentColor !important;
  }

  .styles-panel-action-icon-settings path,
  .styles-panel-action-icon-settings circle {
    stroke: currentColor !important;
  }

  .styles-panel-action-icon-settings circle[fill="white"] {
    fill: var(--menu-color) !important;
  }
`;

const StyleRows = styled.div`
  display: grid;
  gap: 12px;
`;

const StyleRow = styled.div<{ $selected?: boolean; $dashed?: boolean }>`
  --style-row-offset: ${({ $selected }) => ($selected ? "translate(-0.25px, -0.43px)" : "translate(0, 0)")};

  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 56px;
  min-width: 0;
  box-sizing: border-box;
  gap: 16px;
  padding: 0 16px;
  border: ${({ $selected, $dashed }) =>
    $selected ? "2px solid var(--primary-cta)" : $dashed ? "1px dashed var(--grey-2)" : "1px solid var(--grey-3)"};
  border-radius: 8px;
  background: var(--menu-color);
  color: var(--grey-0);
  font: inherit;
  text-align: left;
  cursor: pointer;
  opacity: 0;
  transform: translateY(10px) var(--style-row-offset);
  transition:
    opacity 120ms ease,
    transform 160ms ease,
    background 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease;
  box-shadow: var(--card-drop-shadow);

  ${StylesStatePanel}[data-open="true"] & {
    opacity: 1;
    transform: translateY(0) var(--style-row-offset);
    transition:
      opacity 200ms ease,
      transform 280ms cubic-bezier(0.16, 0.9, 0.18, 1),
      background 160ms ease,
      border-color 160ms ease,
      box-shadow 160ms ease;
  }

  ${StylesStatePanel}[data-open="true"] &:nth-child(1) {
    transition-delay: 210ms, 200ms, 0ms, 0ms, 0ms;
  }

  ${StylesStatePanel}[data-open="true"] &:nth-child(2) {
    transition-delay: 235ms, 225ms, 0ms, 0ms, 0ms;
  }

  ${StylesStatePanel}[data-open="true"] &:nth-child(3) {
    transition-delay: 260ms, 250ms, 0ms, 0ms, 0ms;
  }

  ${StylesStatePanel}[data-open="true"] &:nth-child(4) {
    transition-delay: 285ms, 275ms, 0ms, 0ms, 0ms;
  }

  ${StylesStatePanel}[data-open="true"] &:nth-child(5) {
    transition-delay: 310ms, 300ms, 0ms, 0ms, 0ms;
  }

  ${StylesStatePanel}[data-open="true"] &:nth-child(6) {
    transition-delay: 335ms, 325ms, 0ms, 0ms, 0ms;
  }

  ${StylesStatePanel}[data-open="true"] &:nth-child(7) {
    transition-delay: 360ms, 350ms, 0ms, 0ms, 0ms;
  }

  ${StylesStatePanel}[data-open="true"] &:nth-child(8) {
    transition-delay: 385ms, 375ms, 0ms, 0ms, 0ms;
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 1ms;
    transition-delay: 0ms;

    ${StylesStatePanel}[data-open="true"] & {
      transition-duration: 1ms;
      transition-delay: 0ms;
    }
  }

  &:hover,
  &:focus-visible {
    border-color: var(--primary-cta);
    outline: none;
  }

  .style-row-name {
    display: inline-block;
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    font-size: 16.15px;
    font-weight: 400;
    line-height: 1.35;
    padding-bottom: 2px;
    margin-bottom: -2px;
    text-overflow: ellipsis;
    transform: translateY(-1px);
    white-space: nowrap;
  }

  .style-row-content {
    display: inline-flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    gap: 18px;
    transform: ${({ $selected }) => ($selected ? "translateX(-0.75px)" : "translateX(-1px)")};
  }

  .style-row-tag {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    height: 23px;
    padding: 0 3px;
    border-radius: 5px;
    background: var(--selected-element-background);
    color: var(--selected-element-text);
    font-size: 12px;
    font-weight: 400;
  }

  .style-row-pencil {
    width: 24px;
    height: 24px;
    margin-right: 4px;
    color: var(--grey-0);
  }

  &[data-row-kind="add"] {
    --style-row-offset: translate(-0.25px, -0.43px);

    justify-content: flex-start;
    gap: 12px;
    color: var(--primary-cta-high-contrast);
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    box-shadow: none;
  }

  &[data-row-kind="add"]::before {
    content: "";
    position: absolute;
    inset: -1px;
    border-radius: inherit;
    pointer-events: none;
    background:
      linear-gradient(90deg, var(--grey-2) 50%, transparent 0) top left / 16px 1px repeat-x,
      linear-gradient(90deg, var(--grey-2) 50%, transparent 0) bottom left / 16px 1px repeat-x,
      linear-gradient(0deg, var(--grey-2) 50%, transparent 0) top left / 1px 16px repeat-y,
      linear-gradient(0deg, var(--grey-2) 50%, transparent 0) top right / 1px 16px repeat-y;
  }

  &[data-row-kind="add"] .style-row-name {
    font-weight: 500;
    transform: translate(-1.25px, -1.5px);
  }

  &[data-row-kind="add"] .style-row-add-icon {
    width: 24px;
    height: 24px;
    transform: translateX(-1.25px);

    path {
      fill: var(--primary-cta-high-contrast);
    }
  }
`;

const StyleRowEditButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  margin-right: 0;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--grey-0);
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: var(--primary-cta-high-contrast);
    background: var(--primary-cta-translucent);
    outline: none;
  }

  .style-row-pencil {
    width: 24px;
    height: 24px;
    margin: 0;
  }

  .style-row-pencil path {
    fill: currentColor !important;
  }
`;

const TopBarIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;

  > .icon-button-wrapper,
  #document-sharing-container > button,
  #help-container > button,
  #settings-container > button {
    width: 24px !important;
    min-width: 24px !important;
    height: 24px !important;
    padding: 0 !important;
  }

  > .icon-button-wrapper .icon-wrapper,
  .icon-button-wrapper:hover > .icon-wrapper,
  .icon-button-wrapper:focus-visible > .icon-wrapper {
    width: 32px !important;
    height: 32px !important;
    border-radius: 5px;
  }

  .icon-button-wrapper:active > .icon-wrapper {
    width: 28px !important;
    height: 28px !important;
    border-radius: 5px;
  }

  #document-sharing-container > button:before,
  #help-container > button:before,
  #settings-container > button:before {
    width: 40px;
    height: 40px;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  #document-sharing-container > button:active:before,
  #help-container > button:active:before,
  #settings-container > button:active:before {
    width: 34px !important;
    height: 34px !important;
  }
`;

const StatusGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: var(--grey-0);
  font-size: 14px;
  font-weight: 500;
  opacity: 0.5;
`;

const TourActionSlot = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
`;

const SaveIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  svg {
    width: 16px;
    height: 16px;
  }
`;

const WordCount = styled.span`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const TitleContainer = styled.div`
  min-width: 0;
  flex: 0 1 auto;

  pre {
    margin: 0;
    font-family: inherit;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.3;
    color: var(--grey-0);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 48ch;
  }

  & > div[role="button"] {
    cursor: text;
    padding: 0;
    border-radius: 6px;
    transition: background-color 180ms ease-out;
    outline: none;

    &:hover,
    &:focus-visible {
      background-color: var(--document-background);
    }
    &:focus-visible {
      outline: 2px solid var(--primary-cta-high-contrast);
      outline-offset: 2px;
    }
  }

  form {
    margin: 0;
    max-width: 100%;
  }

  input {
    font-family: inherit;
    font-size: 16px;
    font-weight: 500;
    color: var(--grey-0);
    background-color: var(--document-background);
    border: 1px solid var(--grey-3);
    border-radius: 6px;
    padding: 3px 8px;
    outline: none;
    width: 100%;
    max-width: 48ch;
    box-sizing: border-box;

    &:focus {
      border-color: var(--primary-cta-high-contrast);
    }
  }

  .title-width-measurer {
    position: absolute;
    visibility: hidden;
    pointer-events: none;
    white-space: pre;
    font-family: inherit;
    font-size: 16px;
    font-weight: 500;
    padding: 0 8px;
  }
`;

const SrOnly = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const HomeLink = styled(Link)`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  color: var(--grey-0);
  border-radius: 5px;
  outline: none;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid var(--primary-cta-high-contrast);
    outline-offset: 2px;
  }
`;

const HomeIconWrapper = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 5px;
  transition: all 0.2s ease-in-out;

  svg {
    width: 24px;
    height: 24px;
  }

  path {
    fill: var(--grey-0);
  }
`;

interface BackHomeButtonProps {
  to: string;
  isBackButton: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  ariaLabel?: string;
  title?: string;
}

function BackHomeButton({ to, isBackButton, onClick, ariaLabel, title }: BackHomeButtonProps) {
  return (
    <HomeLink
      to={to}
      aria-label={ariaLabel ?? (isBackButton ? "Back" : "Back to projects")}
      title={title ?? (isBackButton ? "Back to folder" : "Back to projects")}
      onClick={onClick}
    >
      <HomeIconWrapper
        className="project-top-bar-home-icon-wrapper"
        aria-hidden="true"
      >
        <InlineSVG src="/assets/back-arrow.svg" />
      </HomeIconWrapper>
    </HomeLink>
  );
}

interface ProjectNameProps {
  project: ProjectLike;
  onTitleChange?: () => void;
}

const TITLE_INPUT_MEASURE_EXTRA_WIDTH = 4;
const TITLE_INPUT_FALLBACK_HORIZONTAL_CHROME = 20;
const TITLE_INPUT_MIN_WIDTH = 56;
const TITLE_INPUT_MAX_WIDTH = 480;

function ProjectName({ project, onTitleChange }: ProjectNameProps) {
  const [editing, setEditing] = useState(false);
  const [editWidth, setEditWidth] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const titleMeasureRef = useRef<HTMLSpanElement | null>(null);

  const getMeasuredTitleWidth = () => {
    const measuredTextWidth = titleMeasureRef.current?.getBoundingClientRect().width || 0;
    const fallbackTextWidth = project.title.length * 8 + TITLE_INPUT_FALLBACK_HORIZONTAL_CHROME;
    return Math.ceil(
      Math.min(
        TITLE_INPUT_MAX_WIDTH,
        Math.max(TITLE_INPUT_MIN_WIDTH, measuredTextWidth + TITLE_INPUT_MEASURE_EXTRA_WIDTH, fallbackTextWidth)
      )
    );
  };

  const beginEditing = () => {
    setEditWidth(getMeasuredTitleWidth());
    setEditing(true);
  };

  useLayoutEffect(() => {
    if (editing) {
      setEditWidth(getMeasuredTitleWidth());
    }
  }, [editing, project.title]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = (value: string) => {
    if (value !== null && value !== undefined && value.trim() !== "" && value !== project.title) {
      void Promise.resolve(project.update({ title: value, hasAutomaticTitle: false })).finally(() => {
        onTitleChange?.();
      });
    }
    setEditing(false);
  };

  return (
    <TitleContainer data-testid="project-top-bar-title-container">
      <span
        ref={titleMeasureRef}
        className="title-width-measurer"
        aria-hidden="true"
      >
        {project.title || " "}
      </span>
      {editing ? (
        <form
          style={editWidth ? { width: `${editWidth}px` } : undefined}
          onSubmit={(e) => {
            e.preventDefault();
            const input = (e.currentTarget as HTMLFormElement).querySelector(
              "#project-top-bar-title-input"
            ) as HTMLInputElement | null;
            if (input) commit(input.value);
          }}
        >
          <SrOnly htmlFor="project-top-bar-title-input">Project title</SrOnly>
          <input
            id="project-top-bar-title-input"
            ref={inputRef}
            type="text"
            defaultValue={project.title}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setEditing(false);
              }
            }}
          />
        </form>
      ) : (
        <div
          role="button"
          tabIndex={0}
          data-testid="project-top-bar-title"
          aria-label={`${project.title}. Select to edit project title.`}
          onClick={beginEditing}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              beginEditing();
            }
          }}
        >
          <pre>{project.title}</pre>
        </div>
      )}
    </TitleContainer>
  );
}

interface SaveStatusProps {
  saved: SaveState;
}

function SaveStatus({ saved }: SaveStatusProps) {
  if (saved === "saving") {
    return (
      <SaveIndicator data-testid="project-top-bar-save-indicator">
        <InlineSVG
          src="/assets/loader.svg"
          width="16"
          height="16"
        />
        <span>Saving</span>
      </SaveIndicator>
    );
  }
  if (saved === "saved-locally") {
    return (
      <SaveIndicator
        data-testid="project-top-bar-save-indicator"
        title="Connection lost. Your changes will sync automatically when we reconnect."
      >
        <InlineSVG
          src="/assets/check-simple.svg"
          width="16"
          height="16"
        />
        <span>Saved Locally</span>
      </SaveIndicator>
    );
  }
  return (
    <SaveIndicator data-testid="project-top-bar-save-indicator">
      <InlineSVG
        src="/assets/check-simple.svg"
        width="16"
        height="16"
      />
      <span>Saved</span>
    </SaveIndicator>
  );
}

const FALLBACK_STYLE_NAME = STORY_BIBLE_STYLE_NAME;
const DEFAULT_STYLE_ROWS = [
  {
    source: "story_bible" as const,
    id: STORY_BIBLE_STYLE_ID,
    name: STORY_BIBLE_STYLE_NAME,
    kind: "story_bible",
  },
  ...STYLE_GUIDE_BUILT_IN_STYLES.map((style) => ({
    source: "sudowrite" as const,
    id: style.id,
    name: style.name,
    tag: style.tag,
    kind: "sudowrite",
  })),
];

const withoutStoryBibleTag = (row: StyleGuideStyleListItem): StyleGuideStyleListItem => {
  if (row.source !== "story_bible") return row;
  const { tag: _storyBibleTag, ...storyBibleRow } = row;
  return storyBibleRow;
};

const styleIdentityKey = (style: { source: string; id?: string; name: string }) => {
  if ((style.source === "story_bible" || style.source === "sudowrite" || style.source === "user_style") && style.id) {
    return `${style.source}:${style.id}`;
  }

  return `${style.source}:${style.name.trim().toLowerCase()}`;
};

const isEditableStyleRow = (row: StyleGuideStyleListItem) =>
  (row.source === "user_style" || row.source === "story_bible") && Boolean(row.id);

const StyleMatcherLayerFix = createGlobalStyle`
  .ReactModal__Overlay {
    z-index: 100000002 !important;
  }
`;

type StylesFlowState = "list" | "add-modal" | "blank-editor" | "style-editor" | "style-matcher";

function BlankStyleEditorModal({
  projectId,
  onBack,
  onClose,
  onSave,
  onSavedStyle,
  initialStyle,
  readOnlyStyleName = false,
  defaultGenerateSampleWriteModel,
  generatedSample = "",
  onGeneratedSampleChange,
  closeOnBackdrop = false,
  backLabel = "Back to Add New Style",
  closeLabel = "Close blank style editor",
}: {
  projectId: string;
  onBack: () => void;
  onClose: () => void;
  onSave?: (style: EditableStylePayload) => Promise<StyleGuideStyleListItem | void> | StyleGuideStyleListItem | void;
  onSavedStyle?: (style: StyleGuideStyleListItem) => void;
  initialStyle?: StyleGuideStyleListItem;
  readOnlyStyleName?: boolean;
  defaultGenerateSampleWriteModel: string;
  generatedSample?: string;
  onGeneratedSampleChange?: (sample: string) => void;
  closeOnBackdrop?: boolean;
  backLabel?: string;
  closeLabel?: string;
}) {
  const [styleName, setStyleName] = useState(initialStyle?.name ?? "Untitled Style");
  const [activeTab, setActiveTab] = useState<StyleEditorTab>("foundation");
  const [styleId, setStyleId] = useState<string | undefined>(initialStyle?.id);
  const [instructions, setInstructions] = useState(initialStyle?.instructions ?? "");
  const [instructionsWordLimit, setInstructionsWordLimit] = useState(() => getStyleGuideWordLimit(initialStyle?.instructions ?? ""));
  const [simpleControls, setSimpleControls] = useState(() => normalizeSimpleStyleControls(initialStyle?.simpleControls));
  const simpleControlsRef = useRef(simpleControls);
  const [isGeneratingSample, setIsGeneratingSample] = useState(false);
  const [generateSampleError, setGenerateSampleError] = useState("");
  const [generateSampleWriteModel, setGenerateSampleWriteModel] = useState(defaultGenerateSampleWriteModel);
  const [isSavingStyle, setIsSavingStyle] = useState(false);
  const [saveStyleError, setSaveStyleError] = useState("");
  const editorMainRef = useRef<HTMLDivElement | null>(null);
  const instructionsSectionRef = useRef<HTMLDivElement | null>(null);
  const writingSampleSectionRef = useRef<HTMLDivElement | null>(null);
  const instructionsRef = useRef<HTMLTextAreaElement | null>(null);
  const styleIdRef = useRef<string | undefined>(initialStyle?.id);
  const generateSampleWriteModelManuallySelectedRef = useRef(false);
  const lastSavedStyleRef = useRef({
    name: initialStyle?.name ?? "Untitled Style",
    instructions: initialStyle?.instructions ?? "",
    simpleControls: normalizeSimpleStyleControls(initialStyle?.simpleControls),
  });
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const titleId = styleId ? "style-editor-title" : "blank-style-title";

  const saveStyle = async (nextValues?: Partial<Pick<EditableStylePayload, "instructions" | "simpleControls">>) => {
    const nextControls = normalizeSimpleStyleControls(nextValues?.simpleControls ?? simpleControlsRef.current);
    const nextInstructions = nextValues?.instructions ?? instructionsRef.current?.value ?? instructions;
    const nextName = readOnlyStyleName ? (initialStyle?.name ?? STORY_BIBLE_STYLE_NAME) : styleName.trim() || "Untitled Style";

    const runSave = async () => {
      const currentStyleId = styleIdRef.current;
      const lastSavedStyle = lastSavedStyleRef.current;
      if (readOnlyStyleName) {
        if (nextInstructions === lastSavedStyle.instructions && JSON.stringify(nextControls) === JSON.stringify(lastSavedStyle.simpleControls)) {
          return;
        }
      } else if (currentStyleId && nextName === lastSavedStyle.name && nextInstructions === lastSavedStyle.instructions && JSON.stringify(nextControls) === JSON.stringify(lastSavedStyle.simpleControls)) {
        return;
      }

      setIsSavingStyle(true);
      setSaveStyleError("");
      try {
        validateStyleGuideLength(nextInstructions, lastSavedStyle.instructions);
        const payload: EditableStylePayload = {
          name: nextName,
          instructions: nextInstructions,
          ...(Object.keys(nextControls).length || Object.keys(lastSavedStyle.simpleControls).length ? { simpleControls: nextControls } : {}),
        };
        if (currentStyleId) {
          payload.id = currentStyleId;
        }
        const savedStyle = await onSave?.(payload);
        const savedStyleId = savedStyle?.id ?? currentStyleId;
        if (savedStyleId) {
          styleIdRef.current = savedStyleId;
          setStyleId(savedStyleId);
        }
        if (savedStyle) {
          onSavedStyle?.(savedStyle);
        }
        setInstructionsWordLimit(getStyleGuideWordLimit(savedStyle?.instructions ?? nextInstructions));
        lastSavedStyleRef.current = {
          name: savedStyle?.name ?? nextName,
          instructions: savedStyle?.instructions ?? nextInstructions,
          simpleControls: nextControls,
        };
      } catch (error) {
        setSaveStyleError(styleGuideSaveErrorMessage(error));
        throw error;
      } finally {
        setIsSavingStyle(false);
      }
    };

    const nextSave = saveQueueRef.current.catch(() => undefined).then(runSave);
    saveQueueRef.current = nextSave.catch(() => undefined);
    await nextSave;
  };

  const saveAndBack = async () => {
    try {
      await saveStyle();
      onBack();
    } catch {
      // Keep the editor open so the user can retry without losing edits.
    }
  };

  const saveAndClose = async () => {
    try {
      await saveStyle();
      onClose();
    } catch {
      // Keep the editor open so the user can retry without losing edits.
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      void saveAndClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [saveAndClose]);

  useEffect(() => {
    if (generateSampleWriteModelManuallySelectedRef.current) return;
    setGenerateSampleWriteModel(defaultGenerateSampleWriteModel);
  }, [defaultGenerateSampleWriteModel]);

  const updateInstructions = (value: string) => {
    setInstructions(value);
    onGeneratedSampleChange?.("");
    setGenerateSampleError("");
    setSaveStyleError("");
  };

  const generateSamplePreview = async () => {
    const currentInstructions = (instructionsRef.current?.value ?? instructions).trim();
    if (!composeSimpleStyleInstructions(currentInstructions, simpleControlsRef.current)) return;

    setIsGeneratingSample(true);
    onGeneratedSampleChange?.("");
    setGenerateSampleError("");
    try {
      const data = await Auth.fetchWithIdToken("/api/ai/storyEngine/style/generate-sample", {
        projectId,
        storyBibleActive: useStoryBibleStore.getState().isActive,
        instructions: currentInstructions,
        ...(Object.keys(simpleControlsRef.current).length ? { simpleControls: simpleControlsRef.current } : {}),
        writeModel: generateSampleWriteModel,
      });
      const sample = typeof data?.sample === "string" ? data.sample.trim() : "";
      if (!sample) {
        throw new Error("Generated sample was empty");
      }
      onGeneratedSampleChange?.(sample);
    } catch {
      setGenerateSampleError("Failed to generate a sample. Try again.");
    } finally {
      setIsGeneratingSample(false);
    }
  };

  const copyInstructions = () => {
    return copyTextToClipboard(instructionsRef.current?.value ?? instructions);
  };

  const saveStyleOnBlur = (nextValues?: Partial<Pick<EditableStylePayload, "instructions" | "simpleControls">>) => {
    void saveStyle(nextValues).catch(() => undefined);
  };

  const scrollToEditorSection = (section: StyleEditorTab) => {
    setActiveTab(section);
    if (section === "foundation" || section === "limits") return;
    const main = editorMainRef.current;
    const target = section === "instructions" ? instructionsSectionRef.current : writingSampleSectionRef.current;
    if (!main || !target) return;

    const nextTop = Math.max(0, target.getBoundingClientRect().top - main.getBoundingClientRect().top + main.scrollTop - 24);
    if (typeof main.scrollTo === "function") {
      main.scrollTo({ top: nextTop, behavior: "auto" });
      return;
    }
    main.scrollTop = nextTop;
  };

  const updateGenerateSampleWriteModel = (writeModel: string) => {
    generateSampleWriteModelManuallySelectedRef.current = true;
    setGenerateSampleWriteModel(writeModel);
  };

  return ReactDOM.createPortal(
    <StyleEditorSurface
      mode="modal"
      testId="project-top-bar-blank-style-editor"
      titleId={titleId}
      styleName={styleName}
      readOnlyStyleName={readOnlyStyleName}
      onStyleNameChange={(value) => {
        setStyleName(value);
        setSaveStyleError("");
      }}
      onStyleNameBlur={readOnlyStyleName ? () => undefined : saveStyleOnBlur}
      instructions={instructions}
      instructionsWordLimit={instructionsWordLimit}
      simpleControls={simpleControls}
      onSimpleControlsChange={(controls) => {
        const next = normalizeSimpleStyleControls(controls);
        simpleControlsRef.current = next;
        setSimpleControls(next);
        onGeneratedSampleChange?.("");
        setGenerateSampleError("");
        saveStyleOnBlur({ simpleControls: next });
      }}
      instructionsRef={instructionsRef}
      onInstructionsChange={updateInstructions}
      onInstructionsBlur={(value) => saveStyleOnBlur({ instructions: value })}
      activeTab={activeTab}
      onTabSelect={scrollToEditorSection}
      mainRef={editorMainRef}
      instructionsSectionRef={instructionsSectionRef}
      writingSampleSectionRef={writingSampleSectionRef}
      onBack={saveAndBack}
      onClose={saveAndClose}
      onBackdropClick={closeOnBackdrop ? saveAndClose : undefined}
      onCopyInstructions={copyInstructions}
      onGenerateSample={generateSamplePreview}
      generateSampleModelOptions={STYLE_GUIDE_GENERATE_SAMPLE_MODEL_OPTIONS}
      generateSampleModel={generateSampleWriteModel}
      onGenerateSampleModelChange={updateGenerateSampleWriteModel}
      isSaving={isSavingStyle}
      isGeneratingSample={isGeneratingSample}
      generatedSample={generatedSample}
      generateSampleError={generateSampleError}
      saveStyleError={saveStyleError}
      backLabel={backLabel}
      closeLabel={closeLabel}
      generateDisabled={!composeSimpleStyleInstructions(instructions, simpleControls)}
      mainTestId="project-top-bar-style-editor-main"
      instructionsSectionTestId="project-top-bar-style-editor-instructions-section"
      writingSampleSectionTestId="project-top-bar-style-editor-writing-sample-section"
      generatedSampleTestId="project-top-bar-generated-sample-preview"
    />,
    document.body,
  );
}

function StylesStateShell({
  selectedStyleName,
  selectedStyle,
  styleGuideStyles,
  onClose,
  onStyleGuideSelect,
  onBlankStyleSave,
  onStoryBibleStyleSave,
  onGeneratedStyleCreated,
  onStyleGuideSettingsClick,
  initialFlow = "list",
  initialFlowRequestId = 0,
  project,
}: {
  selectedStyleName: string;
  selectedStyle?: StyleGuideSelection | null;
  styleGuideStyles?: StyleGuideStyleListItem[];
  onClose?: () => void;
  onStyleGuideSettingsClick?: () => void;
  onStyleGuideSelect?: (style: StyleGuideSelection) => Promise<void> | void;
  onBlankStyleSave?: (style: EditableStylePayload) => Promise<StyleGuideStyleListItem | void> | StyleGuideStyleListItem | void;
  onStoryBibleStyleSave?: (style: { instructions: string; simpleControls?: SimpleStyleControls }) => Promise<StyleGuideStyleListItem | void> | StyleGuideStyleListItem | void;
  onGeneratedStyleCreated?: (style: StyleGuideStyleListItem) => void;
  initialFlow?: StylesFlowState;
  initialFlowRequestId?: number;
  project: ProjectLike;
}) {
  const [flowState, setFlowState] = useState<StylesFlowState>(initialFlow);
  const [isEntered, setIsEntered] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [styleMatcherInitialSample, setStyleMatcherInitialSample] = useState("");
  const [editingStyle, setEditingStyle] = useState<StyleGuideStyleListItem | null>(null);
  const [blankEditorPreviewKey, setBlankEditorPreviewKey] = useState("blank-editor");
  const [generatedSamplesByStyleKey, setGeneratedSamplesByStyleKey] = useState<Record<string, string>>(() =>
    readGeneratedSampleStorage(project.id),
  );
  const [styleSelectionError, setStyleSelectionError] = useState("");
  const editorOptions = useSudowriteStore((state) => state.editorOptions);
  const currentWriteModel = useWriteModel({ editorOptions });
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const lastHandledInitialFlowRequestRef = useRef(`${initialFlow}:${initialFlowRequestId}`);
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const styleRows = (styleGuideStyles && styleGuideStyles.length > 0 ? styleGuideStyles : DEFAULT_STYLE_ROWS).map(withoutStoryBibleTag);
  const selectedStyleKey = selectedStyle ? styleIdentityKey(selectedStyle) : null;
  const selectedStyleRow = selectedStyleKey
    ? styleRows.find((row) => styleIdentityKey(row) === selectedStyleKey)
    : styleRows.find((row) => row.name.toLowerCase() === selectedStyleName.toLowerCase());
  const selectedStyleIsRow = (row: StyleGuideStyleListItem) =>
    selectedStyleKey
      ? styleIdentityKey(row) === selectedStyleKey
      : row.name.toLowerCase() === selectedStyleName.toLowerCase();
  const selectedStyleMatchesSearch =
    !normalizedSearchQuery || selectedStyleName.toLowerCase().includes(normalizedSearchQuery);
  const visibleRows = styleRows.filter((row) => {
    if (selectedStyleIsRow(row)) {
      return false;
    }

    return !normalizedSearchQuery || row.name.toLowerCase().includes(normalizedSearchQuery);
  });
  const hasSearchResults = selectedStyleMatchesSearch || visibleRows.length > 0;
  const selectedStyleTag = selectedStyleRow?.tag || "";
  const editingStylePreviewKey = editingStyle ? styleIdentityKey(editingStyle) : "";
  const openAddStyleFlow = () => {
    setBlankEditorPreviewKey("blank-editor");
    setFlowState("add-modal");
  };
  const defaultGenerateSampleWriteModel = normalizeStyleGuideGenerateSampleWriteModel(currentWriteModel)
    || normalizeStyleGuideGenerateSampleWriteModel(DEFAULT_WRITE_MODEL)
    || DEFAULT_STYLE_GUIDE_GENERATE_SAMPLE_WRITE_MODEL;
  const setGeneratedSampleForKey = (key: string, sample: string) => {
    if (!key) return;
    setGeneratedSamplesByStyleKey((current) => {
      if (!sample) {
        if (!current[key]) return current;
        const { [key]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [key]: sample };
    });
  };
  const moveGeneratedSampleToStyleKey = (fromKey: string, style: StyleGuideStyleListItem) => {
    const toKey = styleIdentityKey(style);
    if (!fromKey || !toKey || fromKey === toKey) return;
    setBlankEditorPreviewKey(toKey);
    setGeneratedSamplesByStyleKey((current) => {
      const sample = current[fromKey];
      if (!sample) return current;
      const { [fromKey]: _removed, ...rest } = current;
      return { ...rest, [toKey]: sample };
    });
  };

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setIsEntered(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const flowRequestKey = `${initialFlow}:${initialFlowRequestId}`;
    if (lastHandledInitialFlowRequestRef.current === flowRequestKey) return;
    if (flowState !== "list") return;
    lastHandledInitialFlowRequestRef.current = flowRequestKey;

    setFlowState(initialFlow);
    setSearchOpen(false);
    setSearchQuery("");
    setStyleSelectionError("");
    setStyleMatcherInitialSample("");
    setEditingStyle(null);
  }, [flowState, initialFlow, initialFlowRequestId]);

  useEffect(() => {
    setGeneratedSamplesByStyleKey(readGeneratedSampleStorage(project.id));
    setBlankEditorPreviewKey("blank-editor");
  }, [project.id]);

  useEffect(() => {
    writeGeneratedSampleStorage(project.id, generatedSamplesByStyleKey);
  }, [generatedSamplesByStyleKey, project.id]);

  useLayoutEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    }
  }, [searchOpen]);

  const openSearch = () => {
    setStyleSelectionError("");
    setSearchOpen(true);
  };

  const clearOrCloseSearch = () => {
    setStyleSelectionError("");
    if (searchQuery) {
      setSearchQuery("");
      searchInputRef.current?.focus();
      return;
    }

    setSearchOpen(false);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || flowState !== "list") return;
      event.preventDefault();
      if (searchOpen) {
        setSearchQuery("");
        setSearchOpen(false);
        return;
      }
      onClose?.();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [flowState, onClose, searchOpen]);

  const selectStyleRow = async (row: StyleGuideStyleListItem) => {
    setStyleSelectionError("");
    let nextSelection: StyleGuideSelection;
    if (row.source === "story_bible" && row.id) {
      nextSelection = {
        source: "story_bible",
        id: row.id,
        name: row.name,
      };
    } else if (row.source === "sudowrite" && row.id) {
      nextSelection = {
        source: "sudowrite",
        id: row.id,
        name: row.name,
      };
    } else if (row.source === "user_style" && row.id) {
      nextSelection = {
        source: "user_style",
        id: row.id,
        name: row.name,
      };
    } else {
      setStyleSelectionError("This style cannot be selected. Try creating a new style.");
      return;
    }

    try {
      await onStyleGuideSelect?.(nextSelection);
    } catch {
      setStyleSelectionError("Failed to select style. Try again.");
    }
  };

  const handleKeyboardActivation = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    action();
  };

  const openStyleEditor = (row: StyleGuideStyleListItem) => {
    if (!isEditableStyleRow(row)) return;
    setStyleSelectionError("");
    setSearchOpen(false);
    setSearchQuery("");
    setStyleMatcherInitialSample("");
    setEditingStyle(row);
    setFlowState("style-editor");
  };

  const saveEditingStyle = async (payload: EditableStylePayload) => {
    if (editingStyle?.source === "story_bible") {
      const savedStyle = await onStoryBibleStyleSave?.({ instructions: payload.instructions, ...(payload.simpleControls !== undefined ? { simpleControls: payload.simpleControls } : {}) });
      return (
        savedStyle ?? {
          ...editingStyle,
          instructions: payload.instructions,
        }
      );
    }

    return onBlankStyleSave?.(payload);
  };

  const modalFlowOpen = flowState !== "list";
  const openSettings = () => {
    if (modalFlowOpen) return;
    setStyleSelectionError("");
    setSearchOpen(false);
    setSearchQuery("");
    setStyleMatcherInitialSample("");
    setEditingStyle(null);
    onStyleGuideSettingsClick?.();
  };
  const closeFromBackdrop = (event: React.MouseEvent<HTMLDivElement>) => {
    if (modalFlowOpen || event.target !== event.currentTarget) return;
    onClose?.();
  };

  const panel = (
    <StylesStatePanel
      data-testid="project-top-bar-styles-state"
      data-open={isEntered ? "true" : "false"}
      data-modal-open={modalFlowOpen ? "true" : "false"}
      role="dialog"
      aria-label="Styles"
      aria-hidden={modalFlowOpen ? "true" : undefined}
      onClick={closeFromBackdrop}
    >
      <StylesStateContent data-testid="project-top-bar-styles-content">
        <StylesPanelHeader>
          {searchOpen ? (
            <StylesSearchHeader>
              <InlineSVG
                src="/assets/search.svg"
                aria-hidden="true"
              />
              <StylesSearchInput
                ref={searchInputRef}
                aria-label="Search styles"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              <StylesSearchCloseButton
                type="button"
                aria-label={searchQuery ? "Clear style search" : "Close style search"}
                onClick={clearOrCloseSearch}
              >
                <InlineSVG src="/assets/style-guide/settings/settings-close-24.svg" aria-hidden="true" />
              </StylesSearchCloseButton>
            </StylesSearchHeader>
          ) : (
            <>
              <StylesPanelTitle>
                <InlineSVG
                  src="/assets/story-bible/style-icon.svg"
                  aria-hidden="true"
                />
                Styles
              </StylesPanelTitle>
              <StylesPanelActions>
                <StylesIconButton
                  type="button"
                  aria-label="Search styles"
                  onClick={openSearch}
                >
                  <InlineSVG
                    src="/assets/search.svg"
                    className="styles-panel-action-icon-search"
                    aria-hidden="true"
                  />
                </StylesIconButton>
                <StylesIconButton
                  type="button"
                  aria-label="Add style"
                  onClick={openAddStyleFlow}
                >
                  <InlineSVG
                    src="/assets/add.svg"
                    className="styles-panel-action-icon-add"
                    aria-hidden="true"
                  />
                </StylesIconButton>
                <StylesIconButton
                  type="button"
                  aria-label="View all styles"
                  onClick={openSettings}
                >
                  <InlineSVG
                    src="/assets/style-guide/settings/settings-gear-32.svg"
                    className="styles-panel-action-icon-settings"
                    aria-hidden="true"
                  />
                </StylesIconButton>
              </StylesPanelActions>
            </>
          )}
        </StylesPanelHeader>
        <StyleRows>
          {!normalizedSearchQuery && (
            <StyleRow
              role="button"
              tabIndex={0}
              $dashed
              aria-label="Add New"
              data-row-kind="add"
              onClick={openAddStyleFlow}
              onKeyDown={(event) => handleKeyboardActivation(event, openAddStyleFlow)}
            >
              <InlineSVG
                src="/assets/add.svg"
                className="style-row-add-icon"
                aria-hidden="true"
              />
              <span className="style-row-name">Add New</span>
            </StyleRow>
          )}
          {selectedStyleMatchesSearch && (
            <StyleRow
              role="button"
              tabIndex={0}
              $selected
              aria-label={`Selected style: ${selectedStyleName}`}
              onClick={onClose}
              onKeyDown={(event) => handleKeyboardActivation(event, () => onClose?.())}
            >
              <span className="style-row-content">
                <span className="style-row-name">{selectedStyleName}</span>
                {selectedStyleTag && selectedStyleTag !== "Custom" && (
                  <span className="style-row-tag">{selectedStyleTag}</span>
                )}
              </span>
              {selectedStyleRow && isEditableStyleRow(selectedStyleRow) && (
                <StyleRowEditButton
                  type="button"
                  aria-label="Edit style"
                  title={`Edit ${selectedStyleRow.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    openStyleEditor(selectedStyleRow);
                  }}
                >
                  <InlineSVG
                    src="/assets/pencil.svg"
                    className="style-row-pencil"
                    aria-hidden="true"
                  />
                </StyleRowEditButton>
              )}
            </StyleRow>
          )}
          {visibleRows.map((row) => (
            <StyleRow
              key={styleIdentityKey(row)}
              role="button"
              tabIndex={0}
              aria-label={row.tag && row.tag !== "Custom" ? `${row.name} ${row.tag}` : row.name}
              onClick={() => selectStyleRow(row)}
              onKeyDown={(event) => handleKeyboardActivation(event, () => selectStyleRow(row))}
              data-row-kind={row.source === "user_style" ? "custom" : undefined}
            >
              <span className="style-row-content">
                <span className="style-row-name">{row.name}</span>
                {row.tag && row.tag !== "Custom" && <span className="style-row-tag">{row.tag}</span>}
              </span>
              {isEditableStyleRow(row) && (
                <StyleRowEditButton
                  type="button"
                  aria-label="Edit style"
                  title={`Edit ${row.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    openStyleEditor(row);
                  }}
                >
                  <InlineSVG
                    src="/assets/pencil.svg"
                    className="style-row-pencil"
                    aria-hidden="true"
                  />
                </StyleRowEditButton>
              )}
            </StyleRow>
          ))}
          {normalizedSearchQuery && !hasSearchResults && (
            <StylesSearchEmpty>No matching styles</StylesSearchEmpty>
          )}
          {styleSelectionError && <SampleGenerationStatus role="status">{styleSelectionError}</SampleGenerationStatus>}
        </StyleRows>
      </StylesStateContent>
      {flowState === "add-modal" && (
        <AddNewStyleModal
          projectId={project.id}
          onClose={() => setFlowState("list")}
          onSkip={() => setFlowState("blank-editor")}
          onContinueWithSample={(sample) => {
            setStyleMatcherInitialSample(sample);
            setFlowState("style-matcher");
          }}
          onPasteText={() => {
            setStyleMatcherInitialSample("");
            setFlowState("style-matcher");
          }}
        />
      )}
      {flowState === "blank-editor" && (
        <BlankStyleEditorModal
          projectId={project.id}
          onBack={() => setFlowState("add-modal")}
          onClose={() => setFlowState("list")}
          onSave={onBlankStyleSave}
          defaultGenerateSampleWriteModel={defaultGenerateSampleWriteModel}
          generatedSample={generatedSamplesByStyleKey[blankEditorPreviewKey] ?? ""}
          onGeneratedSampleChange={(sample) => setGeneratedSampleForKey(blankEditorPreviewKey, sample)}
          onSavedStyle={(style) => moveGeneratedSampleToStyleKey(blankEditorPreviewKey, style)}
        />
      )}
      {flowState === "style-editor" && editingStyle && (
        <BlankStyleEditorModal
          projectId={project.id}
          key={editingStyle.id}
          initialStyle={editingStyle}
          readOnlyStyleName={editingStyle.source === "story_bible"}
          generatedSample={editingStylePreviewKey ? generatedSamplesByStyleKey[editingStylePreviewKey] ?? "" : ""}
          onGeneratedSampleChange={(sample) => setGeneratedSampleForKey(editingStylePreviewKey, sample)}
          backLabel="Back to styles"
          closeLabel="Close style editor"
          closeOnBackdrop
          onBack={() => setFlowState("list")}
          onClose={() => setFlowState("list")}
          onSave={saveEditingStyle}
          defaultGenerateSampleWriteModel={defaultGenerateSampleWriteModel}
        />
      )}
    </StylesStatePanel>
  );

  const styleMatcherLayer = flowState === "style-matcher" ? (
    <>
      <StyleMatcherLayerFix />
      <StyleMatcher
        close={() => setFlowState("list")}
        setError={setStyleSelectionError}
        setStyle={async (
          instructions: string,
          options?: { writingSample?: string; name?: string },
        ) => {
          const created = await onBlankStyleSave?.({
            name: options?.name?.trim() || "Generated Style",
            instructions,
            writingSample: options?.writingSample,
            source: "generated",
          });
          if (created?.id) {
            onGeneratedStyleCreated?.(created);
          }
        }}
        project={project}
        initialSample={styleMatcherInitialSample}
        variant="styleGuide"
      />
    </>
  ) : null;

  return (
    <>
      {ReactDOM.createPortal(panel, document.body)}
      {styleMatcherLayer && typeof document !== "undefined"
        ? ReactDOM.createPortal(styleMatcherLayer, document.body)
        : null}
    </>
  );
}

export const ProjectTopBar = memo(function ProjectTopBar({
  project,
  saved,
  wordCount,
  highlightedWordCount,
  showWordCount,
  collapsed = false,
  showStyleGuideControl = false,
  selectedStyleName,
  selectedStyle,
  styleGuideStyles,
  styleGuideOpen = false,
  shareSlot,
  helpSlot,
  settingsSlot,
  tourActionSlot,
  tourActionKeepsIcons = false,
  onShareClick,
  onHelpClick,
  onSettingsClick,
  onStyleGuideClick,
  onStyleGuideSettingsClick,
  onStyleGuideSelect,
  onBlankStyleSave,
  onStoryBibleStyleSave,
  onGeneratedStyleCreated,
  initialStyleGuideFlow = "list",
  initialStyleGuideFlowRequestId = 0,
  onProjectTitleChange,
}: ProjectTopBarProps) {
  const location = useLocation<{ from?: string } | undefined>();
  // Inline object selectors return a new ref every call and force TopBar rerenders.
  const { foldersById, getProjectFromFirebaseId, isInTrash, setProjectCover, removeProjectCover } = useAllFoldersStore(
    useShallow((state) => ({
      foldersById: state.foldersById,
      getProjectFromFirebaseId: state.getProjectFromFirebaseId,
      isInTrash: state.isInTrash,
      setProjectCover: state.setProjectCover,
      removeProjectCover: state.removeProjectCover,
    })),
  );
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const coverProject = project?.id ? getProjectFromFirebaseId(project.id) : null;
  const projectBackTarget = useMemo(() => {
    if (!project?.id) return null;

    const storedProject = getProjectFromFirebaseId(project.id);
    const parentFolderId = storedProject?.folder_id;
    if (!storedProject || !parentFolderId) return null;

    const parentFolder = foldersById[parentFolderId];
    if (!parentFolder) return null;

    const isRootFolder = parentFolder?.parent_folder_id === null;
    if (isInTrash(storedProject.id)) {
      return {
        to: isRootFolder ? "/app/projects/deleted" : `/app/projects/deleted/${parentFolderId}`,
        isBackButton: !isRootFolder,
      };
    }

    return {
      to: isRootFolder ? "/app" : `/app/folder/${parentFolderId}`,
      isBackButton: !isRootFolder,
    };
  }, [foldersById, getProjectFromFirebaseId, isInTrash, project?.id]);
  const defaultBackTo = location.state?.from ?? "/app";
  const backTo = projectBackTarget?.to ?? defaultBackTo;
  const isBackButton = projectBackTarget?.isBackButton ?? false;

  const showHighlighted = showWordCount && highlightedWordCount > 0;
  const showTotal = showWordCount && wordCount !== null && !showHighlighted;
  const formattedWordCount =
    wordCount !== null ? wordCount.toLocaleString("en-US") : "";
  const formattedHighlighted = highlightedWordCount.toLocaleString("en-US");
  const resolvedStyleName = selectedStyleName?.trim() || FALLBACK_STYLE_NAME;
  const handleStyleClick = useCallback(() => {
    onStyleGuideClick?.();
  }, [onStyleGuideClick]);
  const handleBackHomeClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    (event) => {
      if (!styleGuideOpen || !onStyleGuideClick) return;

      event.preventDefault();
      onStyleGuideClick();
    },
    [onStyleGuideClick, styleGuideOpen],
  );

  return (
    <Container
      data-testid="project-top-bar"
      className={`project-top-bar${collapsed ? " collapsed" : ""}`}
    >
      <LeftCluster data-testid="project-top-bar-left-cluster">
        <BackHomeButton
          to={backTo}
          isBackButton={isBackButton}
          onClick={handleBackHomeClick}
          ariaLabel={styleGuideOpen ? "Close styles" : undefined}
          title={styleGuideOpen ? "Close styles" : undefined}
        />
        {!styleGuideOpen && coverProject && (
          <ProjectCoverButton
            type="button"
            className="use-component-styles"
            aria-label="Manage cover"
            title="Manage cover"
            onClick={() => setCoverDialogOpen(true)}
          >
            <ProjectCover
              imageUrl={coverProject.cover_image_url}
              title={project.title}
              size="mini"
            />
          </ProjectCoverButton>
        )}
        {!styleGuideOpen && <ProjectName project={project} onTitleChange={onProjectTitleChange} />}
        {coverProject && (
          <ProjectCoverDialog
            open={coverDialogOpen}
            onOpenChange={setCoverDialogOpen}
            projectTitle={project.title}
            currentCoverUrl={coverProject.cover_image_url || null}
            onSave={(payload) => setProjectCover(coverProject, payload)}
            onRemove={() => removeProjectCover(coverProject)}
          />
        )}
      </LeftCluster>
      {showStyleGuideControl && (
        <CenterCluster data-testid="project-top-bar-style-guide-cluster">
          <StyleGuideControlWrap>
            <StyleGuideButton
              type="button"
              data-testid="project-top-bar-style-guide-button"
              $active={styleGuideOpen}
              aria-label={`Styles: ${resolvedStyleName}`}
              aria-expanded={styleGuideOpen}
              onClick={handleStyleClick}
            >
              <InlineSVG
                src="/assets/story-bible/style-icon.svg"
                className="style-guide-leading-icon"
                aria-hidden="true"
              />
              <span className="style-guide-selected-name">{resolvedStyleName}</span>
              <InlineSVG
                src="/assets/chevron-down.svg"
                className="style-guide-chevron"
                aria-hidden="true"
              />
            </StyleGuideButton>
            {styleGuideOpen && (
              <StylesStateShell
                selectedStyleName={resolvedStyleName}
                selectedStyle={selectedStyle}
                styleGuideStyles={styleGuideStyles}
                onClose={handleStyleClick}
                onStyleGuideSelect={onStyleGuideSelect}
                onBlankStyleSave={onBlankStyleSave}
                onStoryBibleStyleSave={onStoryBibleStyleSave}
                onGeneratedStyleCreated={onGeneratedStyleCreated}
                onStyleGuideSettingsClick={onStyleGuideSettingsClick}
                initialFlow={initialStyleGuideFlow}
                initialFlowRequestId={initialStyleGuideFlowRequestId}
                project={project}
              />
            )}
          </StyleGuideControlWrap>
        </CenterCluster>
      )}
      <RightCluster
        data-testid="project-top-bar-right-cluster"
        $styleGuideOpen={styleGuideOpen}
      >
        {tourActionSlot && (
          <TourActionSlot data-testid="project-top-bar-tour-action">
            {tourActionSlot}
          </TourActionSlot>
        )}
        {(!tourActionSlot || tourActionKeepsIcons) && (
          <>
            {!styleGuideOpen && !tourActionSlot && (
              <StatusGroup data-testid="project-top-bar-status">
                <SaveStatus saved={saved} />
                {showTotal && <WordCount>Words: {formattedWordCount}</WordCount>}
                {showHighlighted && (
                  <WordCount>Highlighted words: {formattedHighlighted}</WordCount>
                )}
              </StatusGroup>
            )}
            {!styleGuideOpen && (
              <>
                <TopBarIconSlot data-testid="project-top-bar-share-slot">
                  {shareSlot ?? (
                    <IconButton
                      src="/assets/export.svg"
                      ariaLabel="Share"
                      hoverTipText="Share document"
                      onClick={onShareClick}
                      size={24}
                      iconSize={24}
                    />
                  )}
                </TopBarIconSlot>
                <TopBarIconSlot data-testid="project-top-bar-help-slot">
                  {helpSlot ?? (
                    <IconButton
                      src="/assets/help-icon-v2.svg"
                      ariaLabel="Help"
                      hoverTipText="Help & support"
                      onClick={onHelpClick}
                      size={24}
                      iconSize={24}
                    />
                  )}
                </TopBarIconSlot>
                <TopBarIconSlot data-testid="project-top-bar-settings-slot">
                  {settingsSlot ?? (
                    <IconButton
                      src="/assets/gear-v2.svg"
                      ariaLabel="Settings"
                      hoverTipText="Settings"
                      onClick={onSettingsClick}
                      size={24}
                      iconSize={24}
                    />
                  )}
                </TopBarIconSlot>
              </>
            )}
          </>
        )}
      </RightCluster>
    </Container>
  );
});

export default ProjectTopBar;
